import { useRef } from 'react';
import clonedeep from 'lodash.clonedeep';
import get from 'lodash.get';
import { Product, Item } from 'services/cart';
import packageJSON from '../../package.json';

export const useInputRefs = () => {
  const inputDefaultRef = useRef<HTMLInputElement>(null);
  const inputEmailRef = useRef<HTMLInputElement>(null);
  const inputFirstNameRef = useRef<HTMLInputElement>(null);
  const inputLastNameRef = useRef<HTMLInputElement>(null);
  const inputPhoneRef = useRef<HTMLInputElement>(null);
  const inputPostalCodeRef = useRef<HTMLInputElement>(null);

  const getRef = (Key: any) => {
    let ref;
    switch (Key) {
      case 'email':
        ref = inputEmailRef;
        break;
      case 'firstName':
        ref = inputFirstNameRef;
        break;
      case 'lastName':
        ref = inputLastNameRef;
        break;
      case 'phone':
        ref = inputPhoneRef;
        break;
      case 'postalCode':
        ref = inputPostalCodeRef;
        break;
      default:
        ref = inputDefaultRef;
        break;
    }
    return ref;
  };

  return {
    inputDefaultRef,
    inputEmailRef,
    inputFirstNameRef,
    inputLastNameRef,
    inputPhoneRef,
    inputPostalCodeRef,
    getRef
  };
};

export const getConfiguredOrTranslationValues = (
  languageSettings: any,
  i18n: any,
  translationHeaderPath: string,
  translationSubheaderPath: string,
  config: any,
  configHeaderPath: string,
  configSubheaderPath: string
) => {
  const languages = languageSettings.map((lang: { langCode: any }) => lang.langCode);
  // move the default language to be 1st in the list
  const elementToMove = i18n.language;
  const index = languages.indexOf(elementToMove);
  if (index !== -1) {
    languages.splice(index, 1); // remove the element
    languages.unshift(elementToMove); // add it to the beginning
  }
  const localizedText: any = {};
  languages.forEach((lang: string) => {
    localizedText[lang] = {};
    const translation = i18n.getResourceBundle(`${lang}`, '');
    // check for config
    const configuredHeader = get(config, `${configHeaderPath}.${lang}`);
    const configuredSubheader = get(config, `${configSubheaderPath}.${lang}`);
    const support = get(config, `ticketing.supportName.${lang}`) || (translation && translation.commonText.support);
    // set the header
    if (configuredHeader) {
      localizedText[lang].header = configuredHeader;
      localizedText[lang].isCongfigured = true;
    } else {
      localizedText[lang].header = get(translation, translationHeaderPath);
      if (support && localizedText[lang].header) {
        localizedText[lang].header = localizedText[lang].header.replace('{{support}}', support);
      }
    }
    // set the subheader
    if (configuredSubheader) {
      localizedText[lang].subheader = configuredSubheader;
    } else {
      localizedText[lang].subheader = get(translation, translationSubheaderPath);
      if (support && localizedText[lang].subheader) {
        localizedText[lang].subheader = localizedText[lang].subheader.replace('{{support}}', support);
      }
    }
  });

  return localizedText;
};

export const readTranslationFiles = async (langs: string[]) => {
  const translations: any = {};

  for (const lang of langs) {
    const response = await fetch(`../locales/${lang}/translation.json?v=${packageJSON.version}`);
    const translationStrings = await response.json();
    translations[lang] = translationStrings;
  }

  return translations;
};

export const getCursorPosForMask = (ref: any) => {
  const specialChars = ['(', ')', '-', ' '];
  let count = 0;
  for (let i = 0; i < ref.value.length; i++) {
    if (specialChars.includes(ref.value[i])) {
      if (
        (i > 0 && !isNaN(parseInt(ref.value[i - 1], 10))) ||
        (i < ref.value.length - 1 && !isNaN(parseInt(ref.value[i + 1], 10)))
      ) {
        count++;
      }
    }
  }
  const val = ref.value.replace(/\D/g, '').length + count;
  const characterValue = ref.value.charAt(val);
  const cursorPos = characterValue !== '_' && isNaN(parseInt(characterValue)) ? val + 1 : val;
  return cursorPos;
};

export const handleInputChange = (
  eventValue: string,
  setInputValue: React.Dispatch<React.SetStateAction<string>>,
  inputRef: React.RefObject<HTMLInputElement>
) => {
  const inputEl = inputRef.current;
  if (!inputEl) return;

  const cursorPos: number = inputEl.selectionStart || (inputEl.value ? inputEl.value.length : 0);
  const inputValue: string = inputEl.value;

  if (eventValue === 'delete') {
    if (cursorPos !== 0) {
      const newValue: string = inputValue.slice(0, cursorPos - 1) + inputValue.slice(cursorPos);
      setInputValue(newValue);
      inputEl.value = newValue;
      inputEl.selectionStart = cursorPos - 1;
      inputEl.selectionEnd = cursorPos - 1;
    }
  } else if (eventValue === 'clear') {
    setInputValue('');
    inputEl.value = '';
    inputEl.selectionStart = 0;
    inputEl.selectionEnd = 0;
  } else {
    const newValue: string = inputValue.slice(0, cursorPos) + eventValue + inputValue.slice(cursorPos);
    setInputValue(newValue);
    inputEl.value = newValue;
    inputEl.selectionStart = cursorPos + eventValue.length;
    inputEl.selectionEnd = cursorPos + eventValue.length;
  }

  inputEl.focus();
};

export const handleFormInputChange = (
  e: any,
  apiKey: any,
  formData: any,
  setFormData: React.Dispatch<React.SetStateAction<any>>,
  selectedField: any,
  ref: any
) => {
  const inputEl: HTMLInputElement | null = ref.current;
  if (!inputEl) return;

  const cursorPos: number = inputEl.selectionStart || 0;
  const inputVal: string = inputEl.value;

  if (e.target.value === 'delete' || e.currentTarget.value === 'delete') {
    // Handle backspace
    if (cursorPos !== 0) {
      const newValue: string = inputVal.slice(0, cursorPos - 1) + inputVal.slice(cursorPos);

      setFormData({ ...formData, [apiKey]: newValue });
      inputEl.value = newValue;
      inputEl.selectionStart = cursorPos - 1;
      inputEl.selectionEnd = cursorPos - 1;
    }
  } else if (e.target.value === 'clear') {
    // Clear input field
    setFormData({ ...formData, [apiKey]: '' });
    inputEl.value = '';
    inputEl.selectionStart = 0;
    inputEl.selectionEnd = 0;
  } else {
    // Insert key at cursor position
    if (selectedField && selectedField.maxChar) {
      if (selectedField.maxChar > inputVal.length) {
        const newValue: string = inputVal.slice(0, cursorPos) + e.target.value + inputVal.slice(cursorPos);
        setFormData({ ...formData, [apiKey]: newValue });
        inputEl.value = newValue;
        inputEl.selectionStart = cursorPos + 1;
        inputEl.selectionEnd = cursorPos + 1;
      }
    } else {
      const newValue: string = inputVal.slice(0, cursorPos) + e.target.value + inputVal.slice(cursorPos);
      setFormData({ ...formData, [apiKey]: newValue });
      inputEl.value = newValue;
      inputEl.selectionStart = cursorPos + 1;
      inputEl.selectionEnd = cursorPos + 1;
    }
  }
  inputEl.focus();
};

export const formatCartProducts = (products: Product[]): Product[] => {
  const formattedCart = clonedeep(products) as Product[];
  formattedCart.forEach((product) => {
    if (product.subProducts) {
      product.subProducts.forEach((subPdct) => {
        if (subPdct.items) {
          const itemMap: Map<string, Item> = new Map();
          subPdct.items.forEach((item) => {
            const existing = itemMap.get(item.type);
            if (existing) {
              itemMap.set(item.type, { ...existing, quantity: existing.quantity + 1 });
            } else {
              itemMap.set(item.type, {
                id: item.id,
                typeId: item.typeId,
                type: item.type,
                unitPrice: item.unitPrice,
                quantity: item.quantity || 1,
                removeParams: item.removeParams
              });
            }
          });
          subPdct.items = [...itemMap.values()];
        }
      });
    } else if (product.items) {
      const itemMap: Map<string, Item> = new Map();
      product.items.forEach((item) => {
        const existing = itemMap.get(item.type);
        if (existing) {
          itemMap.set(item.type, { ...existing, quantity: existing.quantity + 1 });
        } else {
          itemMap.set(item.type, {
            id: item.id,
            typeId: item.typeId,
            type: item.type,
            unitPrice: item.unitPrice,
            quantity: item.quantity || 1,
            removeParams: item.removeParams
          });
        }
      });
      product.items = [...itemMap.values()];
    }
  });

  return formattedCart;
};
